"use client";

import { useEffect } from "react";
import Link from "next/link";
import posthog from "posthog-js";
import { useLocale } from "next-intl";
import { Button } from "@/components/ui/button";

export default function DashboardError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const locale = useLocale();
  const isEn = locale === "en";

  useEffect(() => {
    if (typeof window !== "undefined" && typeof console !== "undefined") {
      console.error("Dashboard error boundary caught:", error);
    }
    try {
      posthog.captureException(error, {
        digest: error.digest,
        route: typeof window !== "undefined" ? window.location.pathname : undefined,
        boundary: "dashboard",
      });
    } catch {
      /* posthog not initialized — fail soft */
    }
  }, [error]);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
      <p className="text-sm font-semibold uppercase tracking-widest text-destructive">
        {isEn ? "Error" : "Erreur"}
      </p>
      <h1 className="mt-2 text-2xl font-bold sm:text-3xl">
        {isEn ? "This section crashed" : "Cette section a planté"}
      </h1>
      <p className="mt-3 max-w-md text-muted-foreground">
        {isEn
          ? "You can retry — the rest of the dashboard is unaffected."
          : "Tu peux réessayer — le reste du dashboard n'est pas impacté."}
      </p>
      {error.digest && (
        <p className="mt-2 text-xs text-muted-foreground">
          Code: <span className="font-mono">{error.digest}</span>
        </p>
      )}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <Button onClick={() => reset()}>{isEn ? "Retry" : "Réessayer"}</Button>
        <Button asChild variant="outline">
          <Link href={isEn ? "/en/dashboard" : "/dashboard"}>
            {isEn ? "Back to dashboard" : "Retour au dashboard"}
          </Link>
        </Button>
      </div>
    </div>
  );
}
